/**
 * useRobiChat — shared Robi conversation logic.
 */
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  generateRobiMessageId,
  ROBI_CHAT_USER,
  STORAGE_KEYS,
} from '../constants';
import { messageService } from '../api';
import { useProfile } from '../context/ProfileContext';
import { eventBus } from '../platform/eventBus';
import { ToastService } from '../platform/notifier';
import { analytics, ANALYTICS_EVENTS } from '../analytics';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import {
  getRobiChatMessagesRefresh,
  ROBI_CHAT_HISTORY_CLEARED_EVENT,
  type RobiChatMessagesRefresh,
} from './robiChatHistory';
import { mergeReplyTo } from '../utils/chatReplyPreview';
import type { ApiError } from '../types/api';
import type { ImageSource } from '../types';

import type { ExtendedChatMessage, MessageReplyTo } from '../types/chat';

export interface UseRobiChatOptions {
  /** Bundled or remote avatar for the assistant, supplied by the UI layer. */
  assistantAvatar?: ImageSource | string | null;
  conversationId?: number | null;
}

export interface UseRobiChatResult {
  messages: ExtendedChatMessage[];
  currentUser: { _id: string | number; name: string };
  handleSend: (newMessages?: ExtendedChatMessage[]) => void;
  handleReactionSelect: (messageId: string | number, reaction: string) => void;
  handleDoubleTap: (msg: ExtendedChatMessage) => void;
  isSendingMessage: boolean;
  /**
   * Reloads the thread when history was erased while this screen was not
   * listening (for example, frozen underneath AI Settings). No-ops otherwise.
   */
  refreshIfHistoryCleared: () => void;
}

const mergeServerMessagesWithLocalReplies = (
  serverMessages: ExtendedChatMessage[],
  prevMessages: ExtendedChatMessage[],
): ExtendedChatMessage[] => {
  const prevReplyMap = new Map<string, MessageReplyTo>();
  const prevMatchMap = new Map<string, MessageReplyTo>();
  for (const m of prevMessages) {
    if (m.replyTo) {
      if (m._id != null) prevReplyMap.set(String(m._id), m.replyTo);
      const matchKey = `${String(m.user?._id ?? '')}|${(m.text ?? '').trim()}`;
      prevMatchMap.set(matchKey, m.replyTo);
    }
  }

  return serverMessages.map(m => {
    const matchKey = `${String(m.user?._id ?? '')}|${(m.text ?? '').trim()}`;
    const localReply = (m._id != null ? prevReplyMap.get(String(m._id)) : undefined) || prevMatchMap.get(matchKey);
    const replyTo = mergeReplyTo(m.replyTo, localReply);
    return replyTo ? { ...m, replyTo } : m;
  });
};

const useRobiChat = ({
  assistantAvatar: _assistantAvatar,
  conversationId,
}: UseRobiChatOptions = {}): UseRobiChatResult => {
  const { profile } = useProfile();

  const currentUser = useMemo(() => ({
    _id: profile?.userId ?? 'user-1',
    name: profile?.userName ?? 'You',
    username: profile?.userName ?? null,
  }), [profile?.userId, profile?.userName]);

  const MESSAGES_PAGE_SIZE = 20;
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  /** Invalidates an in-flight load or send so a later erase/send wins. */
  const requestGenerationRef = useRef(0);
  /** Bumped when AI Settings publishes a Messages response, so a stale load cannot restore it. */
  const refreshGenerationRef = useRef(0);
  const appliedRefreshEpochRef = useRef(0);

  const followedAgent = profile?.followedAiAgent;
  const agentId = followedAgent?.agentId;
  const agentName = followedAgent?.userName;
  const agentProfilePicture = followedAgent?.profilePicture;
  const userName = profile?.userName;

  const showFreshChat = useCallback(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    const greetingText = `Hi ${userName || 'AI Buddy'}, and welcome to WLB's natural language AI.\nI can help you do so many things, like plan a diet, suggest an exercise routine, hold you accountable, or just chat.\nIf you choose the option in AI settings to "keep history for 30 days", I can better customize my answers to suit your needs.\nAt any time in your settings, you can choose to "erase all history", which will erase our past chat history.`;
    const greetingMessage: ExtendedChatMessage = {
      _id: `greeting-${conversationId}`,
      text: greetingText,
      createdAt: new Date(),
      user: {
        _id: agentId || ROBI_CHAT_USER._id,
        name: agentName || 'AI Buddy',
        avatar: _assistantAvatar || agentProfilePicture || null,
      },
      system: false,
    };
    setMessages([greetingMessage]);
  }, [conversationId, userName, agentId, agentName, agentProfilePicture, _assistantAvatar]);

  const applyRefreshedMessages = useCallback((serverMessages: ExtendedChatMessage[]) => {
    refreshGenerationRef.current += 1;
    requestGenerationRef.current += 1;
    if (serverMessages.length > 0) {
      setMessages(prev => mergeServerMessagesWithLocalReplies(serverMessages, prev));
      return;
    }
    showFreshChat();
  }, [showFreshChat]);

  const refreshIfHistoryCleared = useCallback(() => {
    const pending = getRobiChatMessagesRefresh();
    if (!pending || pending.epoch === appliedRefreshEpochRef.current) return;
    appliedRefreshEpochRef.current = pending.epoch;
    applyRefreshedMessages(pending.messages);
  }, [applyRefreshedMessages]);

  useEffect(() => {
    const subscription = eventBus.addListener<RobiChatMessagesRefresh>(
      ROBI_CHAT_HISTORY_CLEARED_EVENT,
      payload => {
        if (payload.epoch === appliedRefreshEpochRef.current) return;
        appliedRefreshEpochRef.current = payload.epoch;
        applyRefreshedMessages(payload.messages);
      },
    );
    return () => subscription.remove();
  }, [applyRefreshedMessages]);

  useEffect(() => {
    let mounted = true;
    const refreshGeneration = refreshGenerationRef.current;

    if (conversationId) {
      messageService.getMessages({ 
        conversationId, 
        page: 1, 
        pageSize: MESSAGES_PAGE_SIZE,
        currentUserId: profile?.userId 
      })
        .then(res => {
          if (!mounted || !res || refreshGeneration !== refreshGenerationRef.current) return;
          if (res.messages && res.messages.length > 0) {
            setMessages(prev => mergeServerMessagesWithLocalReplies(res.messages, prev));
          } else {
            showFreshChat();
          }
        })
        .catch(err => {
          console.error('Failed to fetch Robi Chat messages:', err);
        });
    } else {
      setMessages([]);
    }
    return () => { mounted = false; };
  }, [conversationId, profile?.userId, showFreshChat]);

  const handleSend = useCallback(
    (newMessages: ExtendedChatMessage[] = []) => {
      const userMessage = newMessages[0];
      if (!userMessage || !conversationId || isSendingMessage) return;

      const generation = ++requestGenerationRef.current;
      const refreshGeneration = refreshGenerationRef.current;
      const optimisticId = generateRobiMessageId();
      setMessages(prev => [
        {
          _id: optimisticId,
          text: userMessage.text,
          image: userMessage.image,
          imageMetadata: userMessage.imageMetadata,
          replyTo: userMessage.replyTo,
          createdAt: new Date(),
          user: currentUser,
        },
        ...prev,
      ]);

      const replyToId =
        userMessage.replyTo?._id != null && Number.isFinite(Number(userMessage.replyTo._id))
          ? Number(userMessage.replyTo._id)
          : null;

      setIsSendingMessage(true);
      messageService
        .sendMessage({
          conversationId,
          input: {
            text: userMessage.text,
            replyToMessageId: replyToId,
          },
          currentUserId: profile?.userId,
        })
        .then(async () => {
          analytics.trackOnce(
            STORAGE_KEYS.ANALYTICS_FIRST_ROBI_MESSAGE,
            ANALYTICS_EVENTS.ACTIVATION_FIRST_ROBI_MESSAGE,
          );
          try {
            const result = await messageService.getMessages({
              conversationId,
              page: 1,
              pageSize: MESSAGES_PAGE_SIZE,
              currentUserId: profile?.userId,
            });
            if (
              result &&
              result.messages &&
              generation === requestGenerationRef.current &&
              refreshGeneration === refreshGenerationRef.current
            ) {
              setMessages(prev => {
                const merged = mergeServerMessagesWithLocalReplies(result.messages, prev);
                const localGreeting = prev.find(m => String(m._id).startsWith('greeting-'));
                if (localGreeting && !merged.some(m => m._id === localGreeting._id)) {
                  return [...merged, localGreeting];
                }
                return merged;
              });
            }
          } catch (err: unknown) {
            console.error('Failed to fetch updated messages after send:', err);
            const message = getApiErrorMessage(err as ApiError);
            ToastService.show({ type: 'error', message });
          } finally {
            setIsSendingMessage(false);
          }
        })
        .catch((err: unknown) => {
          console.error('Failed to send message to API:', err);
          const message = getApiErrorMessage(err as ApiError);
          ToastService.show({ type: 'error', message });
          if (
            generation === requestGenerationRef.current &&
            refreshGeneration === refreshGenerationRef.current
          ) {
            setMessages(prev => prev.filter(m => m._id !== optimisticId));
          }
          setIsSendingMessage(false);
        });
    },
    [conversationId, currentUser, profile?.userId, isSendingMessage],
  );

  const handleReactionSelect = useCallback(
    (messageId: string | number, reaction: string) => {
      setMessages(prev =>
        prev.map(m => {
          if (m._id !== messageId) return m;
          const nextReaction = m.reaction === reaction ? null : reaction;
          return { ...m, reaction: nextReaction };
        }),
      );
    },
    [],
  );

  const handleDoubleTap = useCallback((msg: ExtendedChatMessage) => {
    setMessages(prev =>
      prev.map(m => {
        if (m._id !== msg._id) return m;
        const nextLiked = !m.isLiked;
        return {
          ...m,
          isLiked: nextLiked,
          reaction: nextLiked
            ? '❤️'
            : m.reaction === '❤️'
              ? null
              : m.reaction,
        };
      }),
    );
  }, []);

  return {
    messages,
    currentUser,
    handleSend,
    handleReactionSelect,
    handleDoubleTap,
    isSendingMessage,
    refreshIfHistoryCleared,
  };
};

export default useRobiChat;
